import { CdkMenuModule } from "@angular/cdk/menu";
import { OverlayModule } from "@angular/cdk/overlay";

import { Component, inject, OnInit, SecurityContext, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { AgGridAngular } from "ag-grid-angular";
import {
  ColDef,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridReadyEvent,
  ISetFilterParams,
  MenuItemDef,
} from "ag-grid-community";
import { GridApi, GridState, RowClassRules, SideBarDef } from "ag-grid-enterprise";
import { first, firstValueFrom, map, switchMap } from "rxjs";
import { dateFormatter } from "src/app/_helpers/aggrid-renderer";
import { AG_GRID_LOCALE_DE } from "src/app/_helpers/aggrid.locale.de";
import { Article, Ticket } from "src/app/_models";
import { Operation, OperationIndexRow } from "src/app/_models/operation.interface";
import { OperationService, ProjectService, TicketService } from "src/app/_services";
import { AgGridThemeService } from "src/app/_services/ag-grid-theme.service";
import {
  AgGridDateTimeEditorComponent,
  AgGridInputEditorComponent,
  AgGridMultilineEditorComponent,
  AgGridPriorityEditorComponent,
  AgGridStatusEditorComponent,
} from "../_shared/ag-grid-editor";
import { OperationsBulkExcelComponent } from "../operations-bulk-excel/operations-bulk-excel.component";

interface BulkExcelDialogState {
  operationIds: number[];
  projectId: number | null;
}

type RowData = OperationIndexRow;

@Component({
  selector: "hawk-ticket-operations",
  templateUrl: "./ticket-operations.component.html",
  standalone: true,
  imports: [AgGridAngular, RouterModule, CdkMenuModule, OverlayModule, OperationsBulkExcelComponent],
  host: {
    class: "app-page",
  },
})
export class TicketOperationsComponent implements OnInit {
  private ticketService = inject(TicketService);
  private operationService = inject(OperationService);
  private projectService = inject(ProjectService);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private ticketIds = toSignal(this.route.queryParams.pipe(map((params) => params["ticketIds"] || [])));
  public hawkTheme = inject(AgGridThemeService).getTheme();
  public data: RowData[] = [];
  public articles: Article[] = [];

  public bulkExcelState = signal<BulkExcelDialogState | null>(null);

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ["columns", "filters"],
  };

  public locale = AG_GRID_LOCALE_DE;
  public storageKey = "ticketOperationsSettings";

  public rowSelection: "single" | "multiple" = "multiple";
  public initialState?: GridState;
  public getRowId = (params: GetRowIdParams<RowData>) => {
    return String(params.data.operation_id);
  };

  public rowClassRules: RowClassRules<RowData> = {
    //
  };

  public defaultColDef: ColDef<RowData> = {
    filter: true,
    filterParams: {
      excelMode: "windows",
    } as ISetFilterParams,
    contextMenuItems: this.getContextMenuItems.bind(this),
  };

  // Column Definitions: Defines the columns to be displayed.
  public colDefs: ColDef<RowData>[] = [
    {
      field: "external_order_nr",
      headerName: "Ext-Nr",
      width: 110,
      hide: true,
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, string>({
        readValue: (data) => data.external_order_nr,
        writeValue: async (data, value) => {
          this.writeTicketValue(data, "external_order_nr", value);
          return value;
        },
      }),
    },
    {
      field: "order_nr",
      headerName: "Hawk-Nr",
      width: 135,
    },
    {
      field: "operation_id",
      headerName: "Einsatz ID",
      width: 135,
    },
    {
      field: "date_created",
      headerName: "Erstelldatum",
      valueFormatter: dateFormatter(),
      width: 140,
    },
    {
      field: "date_sla",
      headerName: "SLA Datum",
      valueFormatter: dateFormatter(),
      width: 140,
      editable: true,
      cellEditor: AgGridDateTimeEditorComponent,
      cellEditorParams: AgGridDateTimeEditorComponent.options<RowData>({
        readValue: (data) => data.date_sla,
        writeValue: async (data, value) => {
          this.writeTicketValue(data, "datesla", value as string); // feld heißt hier wirklich datesla.
          return value;
        },
      }),
    },
    // SLA Auswertung (editable)
    { field: "operations_size", headerName: "Anz. Einsätze" },
    { field: "project_name", headerName: "Projektname" },
    {
      field: "prority_hash.name",
      headerName: "Priorität",
      editable: true,
      cellEditor: AgGridPriorityEditorComponent,
      cellEditorParams: AgGridPriorityEditorComponent.options<RowData>({
        loadOptions: async (data) => {
          return firstValueFrom(this.projectService.getPriorities(data.project_id));
        },
        readValue: (data) => data.prority_hash?.id,
        writeValue: async (data, value) => {
          this.writeTicketValue(data, "priority_id", value);
          return value;
        },
      }),
    },
    {
      field: "status_hash.status_name",
      headerName: "Status",
      editable: true,
      cellEditor: AgGridStatusEditorComponent,
      cellEditorParams: AgGridStatusEditorComponent.options<RowData>({
        loadOptions: async (data) => {
          return firstValueFrom(this.projectService.getStatuses(data.project_id));
        },
        readValue: (data) => data.status_hash?.status_id,
        writeValue: async (data, value) => {
          this.writeTicketValue(data, "status_id", value);
          return value;
        },
      }),
    },
    { field: "store.nr", headerName: "Store Nr" },
    { field: "store.name", headerName: "Store Name" },
    { field: "store.address_street", headerName: "Store Str" },
    { field: "store.address_zip", headerName: "Store PLZ" },
    { field: "store.address_city", headerName: "Store Stadt" },
    { field: "store.address_country", headerName: "Store Land" },
    { field: "store.address_state", headerName: "Store Bundesland" },
    { field: "store.address_lastname", headerName: "Store Nachname" },
    { field: "store.address_firstname", headerName: "Store Vorname", hide: true },

    {
      field: "description",
      headerName: "Ticket Beschreibung",
      autoHeight: true,
      cellClass: "text-wrap",
      width: 1200,
      editable: true,
      cellEditor: AgGridMultilineEditorComponent,
      cellEditorParams: AgGridMultilineEditorComponent.options<RowData>({
        readValue: (data) => data.description,
        writeValue: async (data, value) => {
          this.writeTicketValue(data, "description", value);
          return value;
        },
      }),
      cellRenderer: ({ value }) => {
        const el = document.createElement("div");
        el.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, value || "");
        return el;
      },
    },
    {
      field: "operation_technicians",
      headerName: "Techniker",
      // TODO: editable
    },
    {
      field: "operation_time",
      headerName: "Einsatzdatum",
      valueFormatter: dateFormatter(),
      editable: true,
      cellEditor: AgGridDateTimeEditorComponent,
      cellEditorParams: AgGridDateTimeEditorComponent.options<RowData>({
        readValue: (data) => data.operation_time,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "operation_date", value as string);
          return value;
        },
      }),
    },
    { field: "operation_date_cw", headerName: "KW" },
    {
      field: "operation_description",
      headerName: "Einsatzbeschreibung",
      autoHeight: true,
      cellClass: "text-wrap",
      width: 600,
      editable: true,
      cellEditor: AgGridMultilineEditorComponent,
      cellEditorParams: AgGridMultilineEditorComponent.options<RowData>({
        readValue: (data) => data.operation_description,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "description", value);
          return value;
        },
      }),
      cellRenderer: ({ value }) => {
        const el = document.createElement("div");
        el.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, value || "");
        return el;
      },
    },
    {
      field: "operation_date_travel_start",
      headerName: "Fahrtbeginn",
      valueFormatter: dateFormatter(),
      editable: true,
      cellEditor: AgGridDateTimeEditorComponent,
      cellEditorParams: AgGridDateTimeEditorComponent.options<RowData>({
        readValue: (data) => data.operation_date_travel_start,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "date_travel_start", value as Date);
          return value;
        },
      }),
    },
    {
      field: "operation_date_on_site",
      headerName: "Ankunft",
      valueFormatter: dateFormatter(),
      editable: true,
      cellEditor: AgGridDateTimeEditorComponent,
      cellEditorParams: AgGridDateTimeEditorComponent.options<RowData>({
        readValue: (data) => data.operation_date_on_site,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "date_on_site", value as Date);
          return value;
        },
      }),
    },
    {
      field: "operation_date_repaired",
      headerName: "Wiederhergestellt",
      valueFormatter: dateFormatter(),
      editable: true,
      cellEditor: AgGridDateTimeEditorComponent,
      cellEditorParams: AgGridDateTimeEditorComponent.options<RowData>({
        readValue: (data) => data.operation_date_repaired,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "date_repaired", value as Date);
          return value;
        },
      }),
    },
    {
      field: "operation_date_finished",
      headerName: "Fertig",
      valueFormatter: dateFormatter(),
      editable: true,
      cellEditor: AgGridDateTimeEditorComponent,
      cellEditorParams: AgGridDateTimeEditorComponent.options<RowData>({
        readValue: (data) => data.operation_date_finished,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "date_finished", value as Date);
          return value;
        },
      }),
    },
    {
      field: "operation_traveltime",
      headerName: "Fahrtzeit in Minuten",
      editable: false,
    },
    {
      field: "time_estimation",
      headerName: "kalkulierte Zeit",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, number>({
        type: "number",
        readValue: (data) => Number(data.time_estimation),
        writeValue: async (data, value) => {
          this.writeTicketValue(data, "time_estimation", value);
          return value;
        },
      }),
    },
    {
      field: "operation_worktime",
      headerName: "Arbeitszeit in Minuten",
      editable: false,
    },
    {
      field: "operation_worktime_rounded",
      headerName: "Arbeitszeit gerundet",
      editable: false,
    },
    {
      field: "operation_report",
      headerName: "Technikerreport",
      editable: true,
      cellEditor: AgGridMultilineEditorComponent,
      cellEditorParams: AgGridMultilineEditorComponent.options<RowData>({
        readValue: (data) => data.operation_report,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "report", value);
          return value;
        },
      }),
      cellRenderer: ({ value }) => {
        const el = document.createElement("div");
        el.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, value || "");
        return el;
      },
    },
    { field: "notes", headerName: "Logbucheinträge" },
    { field: "notes_billing", headerName: "Info Buchführung" },
    {
      field: "operation_invoice",
      headerName: "BTM Rechnungsnummer",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, string>({
        readValue: (data) => data.operation_invoice,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice", value);
          return value;
        },
      }),
    },
    {
      field: "operation_invoice_amount",
      headerName: "BTM Rechnungsbetrag",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, number>({
        type: "number",
        readValue: (data) => Number(data.operation_invoice_amount),
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice_amount", value);
          return value;
        },
      }),
    },
    {
      field: "operation_invoice_po_order_amount",
      headerName: "PO Betrag",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, number>({
        type: "number",
        readValue: (data) => Number(data.operation_invoice_po_order_amount),
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice_po_order_amount", value);
          return value;
        },
      }),
    },
    {
      field: "operation_invoice_po15_order_amount",
      headerName: "PO15 Betrag",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, number>({
        type: "number",
        readValue: (data) => Number(data.operation_invoice_po15_order_amount),
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice_po15_order_amount", value);
          return value;
        },
      }),
    },
    {
      field: "operation_invoice_pox_order_amount",
      headerName: "POX Betrag",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, number>({
        type: "number",
        readValue: (data) => Number(data.operation_invoice_pox_order_amount),
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice_pox_order_amount", value);
          return value;
        },
      }),
    },
    {
      field: "operation_invoice_partner",
      headerName: "Partner Rechnungsnummer",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, string>({
        readValue: (data) => data.operation_invoice_partner,
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice_partner", value);
          return value;
        },
      }),
    },
    {
      field: "operation_invoice_partner_amount",
      headerName: "Partner Rechnungsbetrag",
      editable: true,
      cellEditor: AgGridInputEditorComponent,
      cellEditorParams: AgGridInputEditorComponent.options<RowData, number>({
        type: "number",
        readValue: (data) => Number(data.operation_invoice_partner_amount),
        writeValue: async (data, value) => {
          this.writeOperationvalue(data, "invoice_partner_amount", value);
          return value;
        },
      }),
    },
  ];

  private gridApi!: GridApi;

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption("loading", true);
  }

  ngOnInit(): void {
    this.initialState = this.getState();

    this.ticketService.getOperationsForTickets(this.ticketIds()).subscribe({
      next: (data) => {
        this.data = data;
        this.gridApi.setGridOption("loading", false);
      },
      error: (error) => {
        console.error("Error loading operations:", error);
        this.gridApi.setGridOption("loading", false);
      },
    });
  }

  public quicksearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.gridApi.setGridOption("quickFilterText", newValue);
  }

  public saveState() {
    const state = this.gridApi.getState();
    delete state["filter"];
    delete state.pagination.page;
    const serializedState = JSON.stringify(state);
    localStorage.setItem(this.storageKey, serializedState);
  }

  public deleteState() {
    localStorage.removeItem(this.storageKey);
  }

  public getState(): GridState {
    return JSON.parse(localStorage.getItem(this.storageKey));
  }

  private getContextMenuItems(params: GetContextMenuItemsParams): Array<string | MenuItemDef> {
    const result: Array<string | MenuItemDef> = [...params.defaultItems];
    const selection = params.api.getSelectedNodes();
    if (selection.length === 0) return result;

    const rows = selection.map((n) => n.data as RowData).filter(Boolean);
    const internalRows = rows.filter((r) => (r.project_name || '').startsWith('[INTERN]'));
    const externalRows = rows.filter((r) => !(r.project_name || '').startsWith('[INTERN]'));

    // Externe Projekte gruppieren — ID + Name pro Gruppe
    const externalProjects = new Map<number, { id: number; name: string; rows: RowData[] }>();
    for (const row of externalRows) {
      if (row.project_id == null) continue;
      let group = externalProjects.get(row.project_id);
      if (!group) {
        group = { id: row.project_id, name: row.project_name || `Projekt ${row.project_id}`, rows: [] };
        externalProjects.set(row.project_id, group);
      }
      group.rows.push(row);
    }

    const items: MenuItemDef[] = [];

    if (externalProjects.size === 0) {
      // Nur interne Projekte ausgewaehlt — keine Projekt-Vorgabe; Komponente
      // fragt das Projekt im Setup-Schritt ab.
      items.push({
        name: `Versand-/Staging-Artikel via Excel bearbeiten (${internalRows.length} Aufträge, Projekt im Dialog wählen)`,
        action: () => this.openBulkExcel({
          operationIds: internalRows.map((r) => r.operation_id),
          projectId: null,
        }),
      });
    } else if (externalProjects.size === 1) {
      const [external] = externalProjects.values();
      const total = external.rows.length + internalRows.length;
      items.push({
        name: `Versand-/Staging-Artikel via Excel bearbeiten (${total} Aufträge, ${external.name})`,
        action: () => this.openBulkExcel({
          operationIds: [...external.rows, ...internalRows].map((r) => r.operation_id),
          projectId: external.id,
        }),
      });
    } else {
      // Mehrere externe Projekte: pro externem Projekt ein Submenu-Eintrag.
      // Operations aus den anderen externen Projekten werden NICHT mit
      // uebergeben — interne Projekte werden immer mitgefuehrt.
      const subItems: MenuItemDef[] = [];
      for (const external of externalProjects.values()) {
        const total = external.rows.length + internalRows.length;
        subItems.push({
          name: `${external.name} (${total} Aufträge)`,
          action: () => this.openBulkExcel({
            operationIds: [...external.rows, ...internalRows].map((r) => r.operation_id),
            projectId: external.id,
          }),
        });
      }
      items.push({
        name: 'Versand-/Staging-Artikel via Excel bearbeiten',
        subMenu: subItems,
      });
    }

    return [...items, 'separator', ...result];
  }

  protected openBulkExcel(state: BulkExcelDialogState): void {
    this.bulkExcelState.set(state);
  }

  protected closeBulkExcel(): void {
    this.bulkExcelState.set(null);
  }

  protected onBulkExcelImported(): void {
    // Nach erfolgreichem Import: keine Refresh-Logik noetig, da OperationRequirements
    // nicht in der Tabelle angezeigt werden — der Dialog bleibt offen, bis der
    // User schliesst.
  }

  private async writeOperationvalue<K extends keyof Operation>(item: RowData, key: K, value: Operation[K]) {
    const update$ = this.operationService.updateOperation(String(item.operation_id), { [key]: value });
    await firstValueFrom(update$);
    await this.refreshRows([item.id]);
  }

  private async writeTicketValue<K extends keyof Ticket>(item: RowData, key: K, value: Ticket[K]) {
    const update$ = this.ticketService.updateTicket(String(item.id), { [key]: value });
    await firstValueFrom(update$);
    await this.refreshRows([item.id]);
  }

  private async refreshRows(ticketIds: number[]) {
    const refreshed = await firstValueFrom(this.ticketService.getOperationsForTickets(ticketIds.map(String)));
    this.data = this.data.map((item) => {
      const updated = refreshed.find((t) => t.operation_id === item.operation_id);
      if (updated) {
        return updated as any;
      }
      return item;
    });
  }
}
